/**
 * Event series presets.
 *
 * A preset is a saved set of answers for a series we run every season, so creating
 * (say) the next NEPL season is a season number and a start date instead of
 * eighteen fields. Presets only prefill the form - every value stays editable.
 *
 * To add one, append to EVENT_SERIES_PRESETS. `values` are applied verbatim;
 * `seasonWeeks` and `buildName` are applied when the preset asks for a season
 * number and first date.
 */

const NEPL_HTML_TEMPLATE = `<!-- Event Header -->
<div style="background: linear-gradient(135deg, #b91c1c 0%, #ea580c 100%); border-radius: 12px; padding: clamp(1rem, 4vw, 2rem); margin-bottom: 2rem; text-align: center;">
  <h1 style="color: #ffffff; font-size: clamp(1.5rem, 5vw, 2.5rem); font-weight: bold; margin-bottom: 1rem; line-height: 1.2;">New England Pinball League</h1>
  <p style="color: #ffffff; font-size: clamp(0.9rem, 3vw, 1.2rem); margin-bottom: 1rem; line-height: 1.3;">Season [Season] at Capital Pinball Club</p>
  <p style="color: #ffffff; font-size: clamp(0.9rem, 2.5vw, 1.1rem); margin: 0; line-height: 1.3;">Tuesday, [Date] &bull; 7:00 PM</p>
</div>

<!-- Quick Event Details Box -->
<div style="background-color: #f9fafb; border: 2px solid #b91c1c; border-radius: 12px; padding: clamp(1rem, 3vw, 1.5rem); margin-bottom: 2rem;">
  <h3 style="color: #991b1b; font-size: clamp(1.1rem, 3.5vw, 1.3rem); font-weight: 600; margin-bottom: 1rem; line-height: 1.3;">The Details</h3>
  <div style="display: flex; flex-wrap: wrap; gap: 1rem;">
    <div style="flex: 1; min-width: 280px;">
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>When:</strong> Tuesday, [Date] at 7:00 PM</p>
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>Where:</strong> Capital Pinball Club, 70 Pembroke Road, Concord, NH</p>
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>League Fee:</strong> $40 for the season, paid directly to NEPL at nepl.org</p>
    </div>
    <div style="flex: 1; min-width: 280px;">
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>Venue Fee:</strong> Free for members, $15 per session for non-members</p>
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>League Host:</strong> [Host Name]</p>
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>RSVP:</strong> Optional. Groups are drawn once everyone is in, so you can just turn up</p>
    </div>
  </div>
</div>

<!-- How the League Works -->
<div style="margin-bottom: 2rem;">
  <h2 style="text-align: center; font-size: clamp(1.6rem, 4vw, 2.2rem); font-weight: bold; color: #1f2937; margin-bottom: 2rem; line-height: 1.2;">How the League Works</h2>

  <div style="display: flex; flex-wrap: wrap; gap: 2rem; margin-bottom: 2rem;">
    <div style="flex: 1; min-width: 280px; background-color: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: clamp(1rem, 3vw, 1.5rem);">
      <h3 style="color: #1f2937; font-size: clamp(1.1rem, 3vw, 1.3rem); font-weight: 600; text-align: center; margin-bottom: 1rem; line-height: 1.3;">Format</h3>
      <ul style="color: #6b7280; line-height: 1.6; padding-left: 0 !important; margin-left: 0 !important; list-style: none !important; font-size: clamp(0.8rem, 2.5vw, 1rem);">
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">Head-to-head matches in groups of 3-4 players</li>
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">8 week regular season</li>
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">Your two lowest scores are dropped from final standings</li>
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">Miss a week without falling out of contention</li>
      </ul>
    </div>

    <div style="flex: 1; min-width: 280px; background-color: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: clamp(1rem, 3vw, 1.5rem);">
      <h3 style="color: #1f2937; font-size: clamp(1.1rem, 3vw, 1.3rem); font-weight: 600; text-align: center; margin-bottom: 1rem; line-height: 1.3;">Who Should Come</h3>
      <ul style="color: #6b7280; line-height: 1.6; padding-left: 0 !important; margin-left: 0 !important; list-style: none !important; font-size: clamp(0.8rem, 2.5vw, 1rem);">
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">New players welcome, no ranking required</li>
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">All skill levels play in the same groups</li>
      </ul>
    </div>
  </div>
</div>

<!-- Season Standings -->
<div style="background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: clamp(1rem, 3vw, 1.5rem); margin-bottom: 2rem; text-align: center;">
  <h3 style="color: #1f2937; font-size: clamp(1.1rem, 3.5vw, 1.3rem); font-weight: 600; margin-bottom: 1rem; line-height: 1.3;">Standings &amp; Results</h3>
  <p style="color: #6b7280; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.6; margin-bottom: 1rem;">
    Scores are tracked on Matchplay and standings are published by the league at
    <a href="https://nepl.org" style="color: #b91c1c; font-weight: 600;">nepl.org</a>.
    The season ends with a league-wide finals event hosted by NEPL, bringing every location together.
  </p>
</div>

<!-- RSVP Call to Action -->
<div style="background: linear-gradient(135deg, #b91c1c 0%, #ea580c 100%); border-radius: 12px; padding: clamp(1.5rem, 4vw, 2rem); margin-bottom: 2rem; text-align: center;">
  <h3 style="color: #ffffff; font-size: clamp(1.2rem, 3.5vw, 1.5rem); font-weight: 600; margin-bottom: 1rem; line-height: 1.3;">Playing this week?</h3>
  <p style="color: #ffffff; font-size: clamp(0.9rem, 2.5vw, 1.1rem); line-height: 1.6; margin-bottom: 1.5rem;">RSVP if you like, so [Host Name] has a rough idea of numbers. Groups are drawn at the start of the night, and you can always just turn up.</p>
  <a href="[RSVP_LINK]" style="display: inline-block; background-color: #ffffff; color: #b91c1c; padding: 0.85rem 2rem; border-radius: 8px; text-decoration: none; font-weight: 700; font-size: clamp(0.9rem, 2.5vw, 1.1rem);">RSVP for This Week</a>
</div>`;

const QUEER_PINBALL_HTML_TEMPLATE = `<!-- Event Header -->
<div style="background: linear-gradient(135deg, #7c3aed 0%, #db2777 50%, #f59e0b 100%); border-radius: 12px; padding: clamp(1rem, 4vw, 2rem); margin-bottom: 2rem; text-align: center;">
  <h1 style="color: #ffffff; font-size: clamp(1.5rem, 5vw, 2.5rem); font-weight: bold; margin-bottom: 1rem; line-height: 1.2;">Queer Pinball</h1>
  <p style="color: #ffffff; font-size: clamp(0.9rem, 2.5vw, 1.05rem); margin-bottom: 1rem; line-height: 1.6; max-width: 46rem; margin-left: auto; margin-right: auto;">Queer Pinball is a welcoming space for LGBTQ+ folks and allies to hang out, play some amazing pinball machines, and meet new people in a fun and inclusive environment. Whether you have never touched a flipper or you play every week, there is a machine and a group of people here for you.</p>
  <p style="color: #ffffff; font-size: clamp(0.9rem, 2.5vw, 1.1rem); margin: 0; line-height: 1.3;">[Date] &bull; 2:00 PM to 6:00 PM</p>
</div>

<!-- Quick Event Details Box -->
<div style="background-color: #faf5ff; border: 2px solid #7c3aed; border-radius: 12px; padding: clamp(1rem, 3vw, 1.5rem); margin-bottom: 2rem;">
  <h3 style="color: #6b21a8; font-size: clamp(1.1rem, 3.5vw, 1.3rem); font-weight: 600; margin-bottom: 1rem; line-height: 1.3;">The Details</h3>
  <div style="display: flex; flex-wrap: wrap; gap: 1rem;">
    <div style="flex: 1; min-width: 280px;">
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>When:</strong> [Date], 2:00 PM to 6:00 PM</p>
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>Where:</strong> Capital Pinball Club, 70 Pembroke Road, Concord, NH</p>
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>Cost:</strong> Free for members, $15 day pass for non-members</p>
    </div>
    <div style="flex: 1; min-width: 280px;">
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>Host:</strong> [Host Name]</p>
      <p style="margin: 0.25rem 0; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.4;"><strong>RSVP:</strong> Optional. Come as you are, all skill levels welcome</p>
    </div>
  </div>
</div>

<!-- What to Expect -->
<div style="margin-bottom: 2rem;">
  <h2 style="text-align: center; font-size: clamp(1.6rem, 4vw, 2.2rem); font-weight: bold; color: #1f2937; margin-bottom: 2rem; line-height: 1.2;">What to Expect</h2>

  <div style="display: flex; flex-wrap: wrap; gap: 2rem; margin-bottom: 2rem;">
    <div style="flex: 1; min-width: 280px; background-color: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: clamp(1rem, 3vw, 1.5rem);">
      <h3 style="color: #1f2937; font-size: clamp(1.1rem, 3vw, 1.3rem); font-weight: 600; text-align: center; margin-bottom: 1rem; line-height: 1.3;">On the Day</h3>
      <ul style="color: #6b7280; line-height: 1.6; padding-left: 0 !important; margin-left: 0 !important; list-style: none !important; font-size: clamp(0.8rem, 2.5vw, 1rem);">
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">Open play on the club's machines all afternoon</li>
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">Snacks and beverages provided</li>
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">No tournament, no pressure, just open play</li>
      </ul>
    </div>

    <div style="flex: 1; min-width: 280px; background-color: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: clamp(1rem, 3vw, 1.5rem);">
      <h3 style="color: #1f2937; font-size: clamp(1.1rem, 3vw, 1.3rem); font-weight: 600; text-align: center; margin-bottom: 1rem; line-height: 1.3;">Who Should Come</h3>
      <ul style="color: #6b7280; line-height: 1.6; padding-left: 0 !important; margin-left: 0 !important; list-style: none !important; font-size: clamp(0.8rem, 2.5vw, 1rem);">
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">LGBTQ+ folks and allies, everyone welcome</li>
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">All skill levels, including complete beginners</li>
        <li style="margin-bottom: 0.5rem; list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">Come on your own or bring friends</li>
      </ul>
    </div>
  </div>
</div>

<!-- Accessibility -->
<div style="background-color: #faf5ff; border: 1px solid #e5e7eb; border-radius: 12px; padding: clamp(1rem, 3vw, 1.5rem); margin-bottom: 2rem; text-align: center;">
  <h3 style="color: #1f2937; font-size: clamp(1.1rem, 3.5vw, 1.3rem); font-weight: 600; margin-bottom: 1rem; line-height: 1.3;">Cost Should Never Keep You Away</h3>
  <p style="color: #6b7280; font-size: clamp(0.85rem, 2.5vw, 1rem); line-height: 1.6; margin: 0;">The day pass is $15 for non-members, and members get in free. If that is a barrier, get in touch before the event and we will work with you so you can take part. Nobody is turned away.</p>
</div>

<!-- RSVP Call to Action -->
<div style="background: linear-gradient(135deg, #7c3aed 0%, #db2777 50%, #f59e0b 100%); border-radius: 12px; padding: clamp(1.5rem, 4vw, 2rem); margin-bottom: 2rem; text-align: center;">
  <h3 style="color: #ffffff; font-size: clamp(1.2rem, 3.5vw, 1.5rem); font-weight: 600; margin-bottom: 1rem; line-height: 1.3;">Joining us this month?</h3>
  <p style="color: #ffffff; font-size: clamp(0.9rem, 2.5vw, 1.1rem); line-height: 1.6; margin-bottom: 1.5rem;">RSVP if you like, so [Host Name] knows roughly how many to expect. You are always welcome to just turn up.</p>
  <a href="[RSVP_LINK]" style="display: inline-block; background-color: #ffffff; color: #7c3aed; padding: 0.85rem 2rem; border-radius: 8px; text-decoration: none; font-weight: 700; font-size: clamp(0.9rem, 2.5vw, 1.1rem);">RSVP for This Month</a>
</div>`;

export const EVENT_SERIES_PRESETS = [
  {
    id: 'nepl-season',
    label: 'NEPL league season',
    summary: 'New England Pinball League: 8 weekly Tuesday nights, $40 season fee paid to NEPL, same host all season.',
    needsSeason: true,
    seasonWeeks: 8,
    buildName: (seasonNumber) => `New England Pinball League - Season ${seasonNumber || 'NN'}`,
    values: {
      description: 'Weekly New England Pinball League night. Head-to-head match play in groups of 3-4 over an 8 week season, two lowest scores dropped. League fee is paid to NEPL, not the club.',
      html_template: NEPL_HTML_TEMPLATE,
      recurrence_pattern: 'weekly',
      day_of_week: 2,
      time_of_day: '19:00:00',
      duration_minutes: 180,
      event_category: 'pinball',
      max_capacity: null,
      cost_description: '$40/season paid to nepl.org; $15 venue fee per session for non-members, free for members',
      website_url: 'https://nepl.org/locations/qobtciow6cll2kk',
      wordpress_categories: 'NEPL, Pinball, League',
      event_cost: '40',
      featured_image_url: 'https://capitalpinballclub.com/wp-content/uploads/2025/08/NEPL-Season-35-FINALS-Cover-e1776883220636.webp',
      auto_generate_enabled: true,
      generate_advance_days: 60
    }
  },
  {
    id: 'queer-pinball',
    label: 'Queer Pinball (monthly)',
    summary: 'Queer Pinball: first Sunday of the month, 2:00 PM to 6:00 PM, $15 day pass for non-members, same host each month.',
    // Ongoing rather than a bounded season, so there is no season number to ask for and
    // no end date. Generation keeps rolling the next few months forward.
    needsSeason: false,
    values: {
      name: 'Queer Pinball',
      description: 'Monthly afternoon of community, connection, and pinball. A welcoming space for LGBTQ+ folks and allies to hang out, play, and meet new people. All skill levels welcome.',
      html_template: QUEER_PINBALL_HTML_TEMPLATE,
      recurrence_pattern: 'monthly',
      monthly_mode: 'nth_weekday',
      monthly_week_of_month: 1,
      day_of_week: 0,
      time_of_day: '14:00:00',
      duration_minutes: 240,
      event_category: 'pinball',
      max_capacity: null,
      cost_description: 'Free for members, $15 day pass for non-members. Contact us if cost is a barrier.',
      website_url: 'https://capitalpinballclub.com/queer-night/',
      wordpress_categories: 'Open Hours, Queer',
      event_cost: '15',
      featured_image_url: 'https://capitalpinballclub.com/wp-content/uploads/2025/08/Rainbow-Playful-Pride-Information-Session-Instagram-Post-Presentation_20250408_131036_0000-phX4C9-1024x576.webp',
      auto_generate_enabled: true,
      generate_advance_days: 120
    }
  }
];

export const getPreset = (presetId) =>
  EVENT_SERIES_PRESETS.find((preset) => preset.id === presetId) || null;

/**
 * Last date of a season that runs `weeks` occurrences starting on `firstDate`.
 * Returns '' when the inputs are incomplete so the caller can leave the field blank.
 */
export const addWeeks = (firstDate, weeks) => {
  if (!firstDate || !weeks) {
    return '';
  }

  const date = new Date(firstDate + 'T00:00:00');
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  date.setDate(date.getDate() + (weeks - 1) * 7);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Merges a preset (and any season answers) onto the current form values.
 */
export const applyPreset = (preset, { seasonNumber, firstDate } = {}) => {
  if (!preset) {
    return {};
  }

  const applied = { ...preset.values };

  if (preset.buildName) {
    applied.name = preset.buildName(seasonNumber);
  }

  if (applied.html_template && seasonNumber) {
    applied.html_template = applied.html_template.replace(/\[Season\]/g, seasonNumber);
  }

  if (preset.needsSeason) {
    applied.season_start_date = firstDate || '';
    applied.season_end_date = addWeeks(firstDate, preset.seasonWeeks);
  } else {
    // An ongoing series has no season window. Clearing it matters when the form already
    // held a bounded preset: a leftover end date would quietly stop generation at the
    // old season boundary.
    applied.season_start_date = '';
    applied.season_end_date = '';
  }

  return applied;
};
